import React, { useState } from 'react';
import { Input, Typography, Card, Button, Space } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { OrganizationNote } from '../../../graphql/types/organizationNote';
import { LocationHealthNote } from '../../../graphql/query/locationHealthNotes';

const { TextArea } = Input;
const { Text } = Typography;

interface NotesSectionProps {
  notes: OrganizationNote[] | LocationHealthNote[];
  onSave: (content: string) => Promise<void>;
  onUpdate: (id: string, content: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  style?: React.CSSProperties;
  className?: string;
}

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

//@ts-ignore
const NoteCard = styled(Card)`
  .ant-card-body {
    padding: 16px;
  }
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const NoteContent = styled.div`
  margin-bottom: 8px;
`;

const NoteFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #8c8c8c;
  font-size: 12px;
`;

const NotesSection: React.FC<NotesSectionProps> = ({ notes, onSave, onUpdate, onDelete, style, className }) => {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');

  const handleSaveNewNote = async () => {
    if (newNoteContent.trim()) {
      await onSave(newNoteContent);
      setNewNoteContent('');
    }
  };

  const handleUpdateNote = async (id: string) => {
    if (editingContent.trim()) {
      await onUpdate(id, editingContent);
      setEditingNoteId(null);
      setEditingContent('');
    }
  };

  const handleDeleteNote = async (id: string) => {
    await onDelete(id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <NotesContainer style={style} className={className}>
      <TextArea
        value={newNoteContent}
        onChange={(e) => setNewNoteContent(e.target.value)}
        placeholder="Write a new note..."
        autoSize={{ minRows: 3, maxRows: 5 }}
        onPressEnter={(e) => {
          e.preventDefault();
          handleSaveNewNote();
        }}
      />
      <Button type="primary" onClick={handleSaveNewNote} disabled={!newNoteContent.trim()}>
        Add Note
      </Button>

      {notes.map((note) => (
        <NoteCard key={note.id}>
          <NoteHeader>
            <Text strong>{note.createdBy.fullName}</Text>
            <Space>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingNoteId(note.id);
                  setEditingContent(note.content);
                }}
              />
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteNote(note.id)} />
            </Space>
          </NoteHeader>
          <NoteContent>
            {editingNoteId === note.id ? (
              <TextArea
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 5 }}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleUpdateNote(note.id);
                }}
              />
            ) : (
              <Text>{note.content}</Text>
            )}
          </NoteContent>
          <NoteFooter>
            <Text type="secondary">Created: {formatDate(note.createdAt)}</Text>
            {note.updatedAt !== note.createdAt && <Text type="secondary">Updated: {formatDate(note.updatedAt)}</Text>}
          </NoteFooter>
        </NoteCard>
      ))}
    </NotesContainer>
  );
};

export default NotesSection;
